import React from 'react';
import { UsersIcon } from './icons';

export interface PricingCardProps {
  name: string;
  price: number | string;
  priceLabel?: string;
  description: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'coral' | 'neutral';
}

export function PricingCard({
  name,
  price,
  priceLabel = '/ month',
  description,
  variant = 'secondary',
}: PricingCardProps) {
  const variantStyles = {
    primary: {
      border: 'hover:border-primary hover:outline-primary hover:ring-primary',
      text: 'group-hover:text-primary',
      bg: 'bg-primary/25 md:bg-slate-50 md:group-hover:bg-primary/15',
    },
    secondary: {
      border: 'hover:border-secondary hover:outline-secondary hover:ring-secondary',
      text: 'group-hover:text-secondary',
      bg: 'bg-secondary/25 md:bg-slate-50 md:group-hover:bg-secondary/15',
    },
    tertiary: {
      border: 'hover:border-tertiary hover:outline-tertiary hover:ring-tertiary',
      text: 'group-hover:text-tertiary',
      bg: 'bg-tertiary/25 md:bg-slate-50 md:group-hover:bg-tertiary/15',
    },
    coral: {
      border: 'hover:border-coral hover:outline-coral hover:ring-coral',
      text: 'group-hover:text-coral',
      bg: 'bg-coral/25 md:bg-slate-50 md:group-hover:bg-coral/15',
    },
    neutral: {
      border: 'hover:border-slate-500 hover:outline-slate-500 hover:ring-slate-500',
      text: 'group-hover:text-slate-900',
      bg: 'bg-slate-100 md:bg-slate-50 md:group-hover:bg-slate-100',
    },
  };

  const style = variantStyles[variant];

  const containerClasses = `
    group bg-white rounded-lg flex flex-col h-full relative 
    transition-all duration-300 border-2 border-transparent border-solid
    hover:-translate-y-2 hover:shadow-2xl hover:z-20 z-10
    hover:outline hover:outline-2 hover:ring-1 ${style.border}
  `
    .replace(/\s+/g, ' ')
    .trim();

  const titleClasses = 'font-bold text-slate-900 text-lg transition-colors duration-500';
  const priceClasses = 'text-4xl font-bold text-slate-900 transition-colors duration-500';
  const labelClasses = 'text-slate-500 font-medium ml-2 transition-colors duration-500';
  const iconClasses = 'w-5 h-5 text-slate-500 mr-3 flex-shrink-0 transition-colors duration-500';
  const textClasses =
    'flex items-start text-sm text-slate-600 transition-colors duration-500 group-hover:text-slate-700';

  const headerBgClass = `p-6 border-b border-slate-100 text-center transition-colors duration-500 ${style.bg}`;

  return (
    <div className={containerClasses}>
      <div className={headerBgClass}>
        <h4 className={titleClasses}>{name}</h4>
      </div>
      <div className="p-6 flex-grow flex flex-col">
        <div className="mb-6">
          <span className={priceClasses}>
            {typeof price === 'number' && '$'}
            {price}
          </span>
          {priceLabel && <span className={labelClasses}>{priceLabel}</span>}
        </div>
        <div className={textClasses}>
          <UsersIcon className={iconClasses} />
          <span>{description}</span>
        </div>
      </div>
    </div>
  );
}
